import { MOCK_TOKEN_BALANCES } from 'e2e-tests/mocks/wagmi.mocks';
import { Address, erc20Abi, formatUnits, PublicClient } from 'viem';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { CHAINS_BY_ID, DEFAULT_CHAIN } from 'shared/config/config.constants';
import { isMocked } from 'shared/utils/isMocked';
import { CurrencyType, getAvailableCurrencies } from 'shared/web3';

import { jsonStorage } from './persistStorage';

const BALANCE_REFRESH_INTERVAL = 60_000;
const STORE_VERSION = 4;
const STABLE_COINS_PRIORITY = ['USDT', 'USDC', 'USDS'] as const;

interface State {
  availableCurrencies: CurrencyType[];
  currencyForSelect: { label: string; value: string; address: string }[];
  selectedCurrency: (CurrencyType & { balance: number }) | null;
  userBets: { [key: number]: { [symbol: string]: number } };
  balances: Record<string, number>;
  currenciesWithBalance: (CurrencyType & { balance: number })[];
  isLoadingBalances: boolean;
  isUserSelectedCurrency: boolean;

  // actions
  setAvailableCurrencies: (currencies: CurrencyType[]) => void;
  setCurrenciesForSelect: (currencies: { label: string; value: string; address: string }[]) => void;
  setSelectedCurrency: (symbol: string) => void;
  setUserBet: (key: number, bet: number, symbol: string) => void;
  reset: () => void;

  fetchTokenBalances: (
    client: PublicClient,
    walletAddress: Address,
    chainId: number,
  ) => Promise<void>;
  startBalancePolling: (walletAddress: Address, client: PublicClient, chainId: number) => void;
  stopBalancePolling: () => void;
}

const initialState: Omit<
  State,
  | 'setAvailableCurrencies'
  | 'setCurrenciesForSelect'
  | 'setSelectedCurrency'
  | 'setUserBet'
  | 'fetchTokenBalances'
  | 'startBalancePolling'
  | 'stopBalancePolling'
> = {
  availableCurrencies: [],
  currencyForSelect: [],
  selectedCurrency: null,
  userBets: {},
  balances: {},
  currenciesWithBalance: [],
  isLoadingBalances: false,
  isUserSelectedCurrency: false,
  reset: () => {},
};

// ============================
//  Utility: select preferred currency
// ============================
function computeSelectedCurrency(
  currenciesWithBalance: (CurrencyType & { balance: number })[],
  chainId: number | undefined,
  isLoadingBalances: boolean,
): CurrencyType | null {
  if (isLoadingBalances || currenciesWithBalance.length === 0 || !chainId) return null;

  const currenciesOnCurrentChain = currenciesWithBalance.filter((c) => c.chainId === chainId);
  if (!currenciesOnCurrentChain.length) return null;

  const defaultStable = chainId === 8453 ? 'USDC' : 'USDT';
  const fallbackStable = currenciesOnCurrentChain.find((c) => c.symbol === defaultStable) || null;

  const stableWithBalance = STABLE_COINS_PRIORITY.map((symbol) =>
    currenciesOnCurrentChain.find(
      (c) => c.symbol === symbol && (c.balance ?? 0) >= (c.minBet ?? 0),
    ),
  ).find(Boolean);

  const anyWithBalance =
    currenciesOnCurrentChain.find((c) => (c.balance ?? 0) >= (c.minBet ?? 0)) || null;

  return (stableWithBalance as CurrencyType) || anyWithBalance || fallbackStable || null;
}

// ============================
//  Store
// ============================
export const useCurrenciesStore = create<State>()(
  persist(
    immer(
      devtools((set, get) => {
        let pollingInterval: NodeJS.Timeout | null = null;

        const fetchTokenBalances = async (
          client: PublicClient,
          walletAddress: Address,
          chainId: number,
        ) => {
          if (!walletAddress || !chainId) return;

          console.log('[CurrenciesStore] Fetching balances...');

          const chain = CHAINS_BY_ID[chainId] ?? DEFAULT_CHAIN;
          const chainTokens: CurrencyType[] = getAvailableCurrencies(chain).map((t) => ({
            ...t,
            address: t.address.toLowerCase() as `0x${string}`,
            bet: Array.isArray(t.bet) ? t.bet.map(String) : [String(t.bet)],
          }));

          if (!chainTokens.length) {
            set((s) => {
              s.availableCurrencies = [];
              s.currencyForSelect = [];
              s.balances = {};
              s.currenciesWithBalance = [];
              s.selectedCurrency = null;
            });
            return;
          }

          const currencyForSelect = chainTokens.map((c) => ({
            label: c.symbol,
            value: c.symbol,
            address: c.address,
          }));

          set((s) => {
            s.availableCurrencies = chainTokens;
            s.currencyForSelect = currencyForSelect;
            s.isLoadingBalances = true;
          });

          // ============================
          // MOCK PATH
          // ============================
          if (isMocked) {
            const balancesMap: Record<string, number> = {};
            for (const token of chainTokens) {
              const key = token.address.toLowerCase();
              const mockValue = MOCK_TOKEN_BALANCES[key];
              balancesMap[token.address] = mockValue
                ? Number(formatUnits(mockValue, token.decimals))
                : 0;
            }

            const currenciesWithBalance = chainTokens.map((token) => ({
              ...token,
              balance: balancesMap[token.address] ?? 0,
            }));

            set((s) => {
              s.balances = balancesMap;
              s.currenciesWithBalance = currenciesWithBalance;

              const prevSelected = s.selectedCurrency;
              const updatedSelected = prevSelected
                ? currenciesWithBalance.find((c) => c.symbol === prevSelected.symbol)
                : null;

              if (s.isUserSelectedCurrency) {
                if (updatedSelected) {
                  s.selectedCurrency = { ...updatedSelected };
                }
              } else {
                s.selectedCurrency = computeSelectedCurrency(
                  currenciesWithBalance,
                  chainId,
                  false,
                ) as (CurrencyType & { balance: number }) | null;
              }
              s.isLoadingBalances = false;
            });
            return;
          }

          // ============================
          // REAL NETWORK CALL
          // ============================
          if (!client) return;

          try {
            const contracts = chainTokens.map((token) => ({
              abi: erc20Abi,
              address: token.address,
              functionName: 'balanceOf' as const,
              args: [walletAddress] as const,
            }));

            const results = await (client as any).multicall({
              allowFailure: true,
              contracts,
            });

            const balancesMap: Record<string, number> = {};
            results.forEach((r: any, i: number) => {
              const token = chainTokens[i];
              balancesMap[token.address] =
                r.status === 'success' ? Number(formatUnits(r.result, token.decimals)) : 0;
            });

            const currenciesWithBalance = chainTokens.map((token) => ({
              ...token,
              balance: balancesMap[token.address] ?? 0,
            }));

            set((s) => {
              s.balances = balancesMap;
              s.currenciesWithBalance = currenciesWithBalance;

              const prevSelected = s.selectedCurrency;
              const updatedSelected = prevSelected
                ? currenciesWithBalance.find((c) => c.symbol === prevSelected.symbol)
                : null;

              if (s.isUserSelectedCurrency) {
                if (updatedSelected) {
                  s.selectedCurrency = { ...updatedSelected };
                }
              } else {
                s.selectedCurrency = computeSelectedCurrency(
                  currenciesWithBalance,
                  chainId,
                  false,
                ) as (CurrencyType & { balance: number }) | null;
              }
            });
          } catch (err) {
            console.error('[CurrenciesStore] Failed to fetch balances:', err);
          } finally {
            set((s) => {
              s.isLoadingBalances = false;
            });
          }
        };

        const startBalancePolling = (
          walletAddress: Address,
          client: PublicClient,
          chainId: number,
        ) => {
          stopBalancePolling();
          fetchTokenBalances(client, walletAddress, chainId);
          pollingInterval = setInterval(() => {
            fetchTokenBalances(client, walletAddress, chainId);
          }, BALANCE_REFRESH_INTERVAL);
        };

        const stopBalancePolling = () => {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
          }
        };

        return {
          ...initialState,

          setAvailableCurrencies: (currencies) =>
            set((s) => {
              s.availableCurrencies = currencies;
            }),

          setCurrenciesForSelect: (currencies) =>
            set((s) => {
              s.currencyForSelect = currencies;
            }),

          setSelectedCurrency: (symbol) =>
            set((s) => {
              s.selectedCurrency =
                s.currenciesWithBalance.find((el) => el.symbol === symbol) || null;
              s.isUserSelectedCurrency = true;
            }),

          setUserBet: (key, bet, symbol) =>
            set((s) => {
              s.userBets[key] = { [symbol]: bet };
            }),

          reset: () => set(() => ({ ...initialState })),

          fetchTokenBalances,
          startBalancePolling,
          stopBalancePolling,
        };
      }),
    ),
    {
      name: 'currenciesStore',
      storage: jsonStorage(),
      version: STORE_VERSION,
      partialize: (state) => ({
        availableCurrencies: state.availableCurrencies,
        currencyForSelect: state.currencyForSelect,
        userBets: state.userBets,
        balances: state.balances,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version < STORE_VERSION) return { ...initialState };
        return persistedState as State;
      },
    },
  ),
);
